import { getAuth } from 'firebase/auth';
import { captureAsyncError } from '../../../utils/errorHandler';
import { ItemListType } from '../../../types/common';
import type { RowCountChange } from '../../../utils/pattern/changedItemIds';

/**
 * 子パターンの取得ルール提案（#2967）。
 *
 * 既存の AI改善（handleSuggestPatternRule）とは別物。
 * AI改善は「間違えた項目について、汎用的なルールを提案する」が、
 * こちらは「帳票を見て、全項目について、このレイアウト専用の具体的なルールを提案する」。
 * 位置指定や欄名の指定を積極的に使うため、修正履歴を必要としない。
 */

export interface SubPatternRuleSuggestion {
  field_name: string;
  current_rule: string;
  suggested_rule: string;
  reason: string;
  /** この帳票に印字されている見出し（#2967）。親と同じ・読み取れない場合は空文字 */
  printed_item_name?: string;
}

export interface SuggestSubPatternRulesResponse {
  success: boolean;
  suggestions: SubPatternRuleSuggestion[];
  error?: { message: string };
}

/** 項目リストをAPIに渡す形（snake_case）に変換する */
function convertItemListForApi(itemList: ItemListType[]): Array<{
  id: string;
  parent_value: string;
  extraction_rule: string;
  type: string;
  children: Array<{ id: string; child_value: string; extraction_rule: string }>;
}> {
  return (
    itemList
      // AI読取の対象外（手入力欄・計算フィールド）は取得ルールが効かないため渡さない
      .filter(item => item.is_active !== false && item.is_ocr_target !== false)
      .map(item => ({
        id: item.id,
        parent_value: item.parentValue,
        extraction_rule: item.extractionRule ?? '',
        type: item.type ?? 'string',
        children: (item.children ?? [])
          .filter(child => child.is_active !== false && child.is_ocr_target !== false)
          .map(child => ({
            id: child.id,
            child_value: child.childValue,
            extraction_rule: child.extractionRule ?? '',
          })),
      }))
  );
}

/**
 * 行数を直した明細の状態をAPIに渡す形（snake_case）に変換する（#3380）
 */
function convertRowCountChangesForApi(rowCountChanges: Record<string, RowCountChange>): Record<
  string,
  {
    before_row_count: number;
    after_row_count: number;
    before_sample_rows: Array<Record<string, string>>;
  }
> {
  return Object.fromEntries(
    Object.entries(rowCountChanges).map(([itemId, change]) => [
      itemId,
      {
        before_row_count: change.beforeRowCount,
        after_row_count: change.afterRowCount,
        before_sample_rows: change.beforeSampleRows,
      },
    ]),
  );
}

export const handleSuggestSubPatternRules = captureAsyncError(
  async (
    resultId: string,
    itemList: ItemListType[],
    rowCountChanges: Record<string, RowCountChange> = {},
  ): Promise<SuggestSubPatternRulesResponse> => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('ユーザー認証が必要です');
    }

    const idToken = await user.getIdToken();
    const functionUrl = process.env.REACT_APP_FUNCTION_URL;
    if (!functionUrl) {
      throw new Error('REACT_APP_FUNCTION_URL environment variable is not set');
    }

    const response = await fetch(`${functionUrl}/suggestSubPatternRules`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        resultId,
        patternItemList: convertItemListForApi(itemList),
        rowCountChanges: convertRowCountChangesForApi(rowCountChanges),
      }),
    });

    if (!response.ok) {
      throw new Error('取得ルールの提案に失敗しました。手入力で作成してください。');
    }

    const result = (await response.json()) as SuggestSubPatternRulesResponse;
    if (result.error) {
      throw new Error(result.error.message || '取得ルールの提案に失敗しました。');
    }

    return result;
  },
);
